import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { PointTransaction } from '../../../../models/point-transaction';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { pointTransactionType } from '../../../../models/enums';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { CustomerService } from '../../../../services/customers/customer-service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-customer-points-transaction-history',
  imports: [TableComponent],
  templateUrl: './customer-points-transaction-history.html',
  styleUrl: './customer-points-transaction-history.scss'
})
export class CustomerPointsTransactionHistory implements OnInit {

  @ViewChild(TableComponent) table!: TableComponent<PointTransaction>;
  @Input() customerID: string | undefined

  params = [
    "page",
    "size",
    'sortBy',
    'sortDirection',
    'transactionType',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    transactionType: "transactionType",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  tableColumns: DynamicColumn<PointTransaction>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    {
      header: 'Transaction type',
      field: 'transactionType',
      sortable: true,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'select',
      filterOptions: enumToOptions(pointTransactionType),
    },
    {
      header: 'Amount',
      field: 'amount',
      sortable: true,
    },
    {
      header: 'Balance after',
      field: 'balanceAfter',
      sortable: true,
    },
    {
      header: 'Created at',
      field: 'createdAt',
      sortable: true,
      isDateTime: true,
    },
  ];

  pointTransactionsList = signal<PointTransaction[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  private lastParams = new BehaviorSubject<Record<string, unknown> | null>(null);
  
  private customerService = inject(CustomerService);
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getPointTransactionsList();
  }

  getPointTransactionsList = () => {
    this.activatedRoute.queryParams.pipe(
      debounceTime(0),
      filter(params => params['tab'] === '5'),

      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

        filter((queryParams) => {
    // 🔥 only continue if params changed
    if (JSON.stringify(this.lastParams.value) === JSON.stringify(queryParams)) {
      return false;
    }
    this.lastParams.next(queryParams);
    return true;
  }),

      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.customerService.getCustomerPointTransactionHistory(this.customerID!, params);
      })
    )
      .subscribe((res) => {
        this.pointTransactionsList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll = () => {
    this.table?.clearAllFilters();
  }
}
